import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import moment from 'moment-timezone';

import { useCalendarUI } from '../context/CalendarUIContext';
import { useEventMutations } from './useEventMutations';
import { useAttendeesAndGroups } from './useAttendeesAndGroups';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { uploadFiles } from 'utils/filesUtils';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { useCalendarState } from '../context/CalendarContext';
import { deepEqual, parseRemiderPayload, parseReminderData } from 'utils/utils';
import { useNavbarContext } from 'context/NavbarContext';
import { combineDateAndTime } from 'utils/dateTimeUtils';

// Note: all day event all date in YYYY-MM-DD format

/**
 * Round time to next 15-minute interval
 */
const roundToNext15Minutes = (date) => {
  const d = new Date(date);
  const minutes = d.getMinutes();
  const remainder = minutes % 15;

  // Always clear seconds and milliseconds for consistency
  d.setSeconds(0);
  d.setMilliseconds(0);

  // If already at a 15-minute mark (0, 15, 30, 45), return as-is
  if (remainder === 0) return d;

  // Otherwise, round up to next 15-minute mark
  d.setMinutes(minutes + (15 - remainder));
  return d;
};

/**
 * Custom hook for event form logic
 */
export const useEventFormLogic = () => {
  const { modals, selectedEvent, eventFormMode, closeEventFormModal } = useCalendarUI();
  const { isFromLead, caseDetails } = useCalendarState();
  const { createEvent, updateEvent, isCreating, isUpdating } = useEventMutations();
  const { userDetails } = useUserDetailsContext();
  const { addToast } = useToast();
  const { showConfirmDialog } = useNavbarContext();

  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  /**
   * Get default times for new events in user's timezone
   */
  const getDefaultTimes = () => {
    const timezone = userDetails?.timezone || 'America/New_York';
    // Get current time in user's timezone
    const nowInUserTz = moment.tz(timezone).toDate();
    const start = roundToNext15Minutes(nowInUserTz);
    const end = new Date(start);
    end.setMinutes(end.getMinutes() + 30);
    return { start, end };
  };

  /**
   * Default form values
   */
  const getDefaultValues = () => {
    const times = getDefaultTimes();
    return {
      title: '',
      meeting_start_time: times.start,
      meeting_end_time: times.end,
      when: times.start,
      till: null,
      case: null,
      case_name: '',
      not_linked_to_case: false,
      event_type: null,
      all_day: false,
      firm_wide: false,
      location: '',
      conference_room_id: '',
      conference_url: '',
      practice_area: '',
      selected_attendees_list: [],
      contacts: [],
      firm_users: [],
      groups: [],
      description: '',
      attachments: [],
      is_recurring: false,
      recurrence_keys: {
        recurrence_frequency: '',
        recurrence_limit: null,
        week_occurrence: '',
        weekday: '',
      },
      reminder: [],
    };
  };

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    trigger,
    resetField,
    formState: { errors },
  } = useForm({
    defaultValues: getDefaultValues(),
    mode: 'onChange',
  });

  // Get form values to watch for case selection
  const caseValue = watch('case');
  const caseId = caseValue?.id || caseDetails?.case_id || null;

  // Fetch attendees and groups based on case selection
  const { data: attendeesList = [], isLoading: isLoadingAttendees } = useAttendeesAndGroups(caseId, modals.eventForm);

  // Initialize form on modal open
  useEffect(() => {
    if (!modals.eventForm) return;

    if (eventFormMode === 'edit' && selectedEvent) {
      // Map API response directly to form structure
      const apiData = selectedEvent;

      // Convert event dates
      let startDate = new Date(apiData.meeting_start_time || apiData.when || apiData.start);
      let endDate = new Date(apiData.meeting_end_time || apiData.till || apiData.end || startDate);

      if (apiData.all_day) {
        startDate = moment.tz(`${apiData?.meeting_start_time} 09:00`, 'YYYY-MM-DD HH:mm', userDetails?.timezone).toDate();
        endDate = moment.tz(`${apiData?.meeting_end_time} 10:00`, 'YYYY-MM-DD HH:mm', userDetails?.timezone).toDate();
      }

      // Map case data if present
      const caseData = apiData.case_id
        ? {
            id: apiData.case_id,
            case_name: apiData.case_name,
            is_lead: apiData.is_lead,
            practice_area: apiData.practice_area?.value || apiData.practice_area?.practice_area_id || null,
            label: apiData.case_name,
          }
        : null;

      // Map event type - handle nested structure
      const eventTypeData =
        apiData.event_type && Object.keys(apiData.event_type).length > 0
          ? {
              event_type: apiData.event_type.event_type
                ? {
                    value: apiData.event_type.event_type,
                    label: apiData.event_type._event_type_,
                    icon: apiData.event_type._event_type_icon_,
                  }
                : null,
            }
          : null;

      // Map practice area
      const practiceAreaData = apiData.practice_area
        ? {
            value: apiData.practice_area,
            label: apiData._practice_area_,
            practice_area_id: apiData.practice_area,
            practice_area_name: apiData._practice_area_,
          }
        : null;

      // Map location
      const locationData = apiData?.location
        ? {
            value: apiData.location,
            label: apiData._office_location_ || apiData._contact_title_,
            is_contact: Boolean(apiData._contact_title_),
          }
        : null;

      reset({
        title: apiData.title || '',
        meeting_start_time: startDate,
        meeting_end_time: endDate,
        when: startDate,
        till: apiData.all_day ? endDate : null,
        case: caseData,
        case_name: apiData.case_name || '',
        not_linked_to_case: apiData.not_linked_to_case || false,
        event_type: eventTypeData,
        all_day: apiData.all_day || false,
        firm_wide: apiData.firm_wide || false,
        location: locationData,
        conference_room_id: apiData.conference_room_id || '',
        conference_url: apiData.conference_url || '',
        practice_area: practiceAreaData,
        selected_attendees_list:
          [...(apiData.guests || []), ...(apiData?.groups || [])]?.map(
            (guest) => guest?.cognito_username || guest?.contact_id || guest?.group_id
          ) || [],
        contacts: apiData.guests?.filter((guest) => !guest?.firm_user_id) || [],
        firm_users: apiData.guests?.filter((guest) => guest?.firm_user_id) || [],
        groups: apiData.groups || [],
        description: apiData.description || '',
        attachments: apiData.attachments || [],
        is_recurring: apiData.is_recurring || false,
        recurrence_keys: {
          recurrence_frequency: apiData.recurrence_keys?.recurrence_frequency || '',
          recurrence_limit: apiData.all_day
            ? moment.tz(`${apiData?.recurrence_keys?.recurrence_limit} 09:00`, 'YYYY-MM-DD HH:mm', userDetails?.timezone).toDate()
            : apiData.recurrence_keys?.recurrence_limit || null,
          week_occurrence: apiData.recurrence_keys?.week_occurrence || '',
          weekday: apiData.recurrence_keys?.weekday || '',
        },
        reminder: parseReminderData(apiData.reminder || []),
        is_lead: isFromLead ? isFromLead : apiData.is_lead,
        sk: apiData.sk || '',
        pk: apiData.pk || '',
        event_id: apiData.event_id || '',
        event_rrule_id: apiData.event_rrule_id || '',
      });
    } else {
      // Create mode
      const times = selectedEvent?.start
        ? {
            // Use the calendar-selected time directly without rounding
            start: new Date(selectedEvent.start),
            end: selectedEvent.end ? new Date(selectedEvent.end) : new Date(new Date(selectedEvent.start).getTime() + 30 * 60000), // Add 30 minutes
          }
        : getDefaultTimes();

      reset({
        ...getDefaultValues(),
        meeting_start_time: times.start,
        meeting_end_time: times.end,
        when: times.start,
        all_day: selectedEvent?.allDay || false,
        till: selectedEvent?.allDay ? times.end : null,
        reminder: parseReminderData(userDetails?.reminder),
      });
    }
  }, [modals.eventForm, eventFormMode, selectedEvent, reset, isFromLead, userDetails]);

  // Form submission
  const onSubmit = async (data) => {
    try {
      const isAllDay = data.all_day;
      // Handle file uploads if there are attachments
      if (data.attachments && data.attachments.length > 0) {
        try {
          setIsUploading(true);
          setUploadProgress(0);

          const caseIdForUpload = data.case?.id || '';
          const userId = userDetails?.user_id || userDetails?.contact_id || userDetails?.firm_user_id || '';

          const pendingAttachments = data?.attachments?.filter((attachment) => attachment instanceof File);
          const alreadyUploadedAttachments = data?.attachments?.filter(
            (attachment) => attachment instanceof Object && attachment.isFileUploaded
          );
          if (pendingAttachments.length > 0) {
            const uploadedFiles = await uploadFiles(pendingAttachments, 'calendar', caseIdForUpload, userId, (progress) => {
              setUploadProgress(progress);
            });
            data.attachments = [...alreadyUploadedAttachments, ...uploadedFiles];
          } else {
            data.attachments = alreadyUploadedAttachments;
          }
          setIsUploading(false);
          setUploadProgress(100);
        } catch (uploadError) {
          setIsUploading(false);
          setUploadProgress(0);
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, uploadError.message || 'Failed to upload attachments');
          return; // Stop form submission if file upload fails
        }
      }

      let eventData = {
        ...data,
        case_id: data.case?.id || caseDetails?.case_id || data?.case_id,
        case_name: data?.case?.case_name || caseDetails?.case_description?.case_name || '',
        is_lead: isFromLead || data?.case?.is_lead,
        when: data.when,
        recurrence_keys: data.is_recurring
          ? {
              ...data.recurrence_keys,
              recurrence_limit: isAllDay
                ? moment.tz(data.recurrence_keys?.recurrence_limit, userDetails?.timezone).format('YYYY-MM-DD')
                : moment.tz(data.recurrence_keys?.recurrence_limit, userDetails?.timezone).endOf('day').utc().toDate(),
            }
          : null,
        guests: [...data.contacts, ...data.firm_users],
        till: data.till,
        event_type: data.event_type
          ? {
              event_type: data.event_type?.event_type?.value,
            }
          : null,
        location: data.location?.value || null,
        reminder: parseRemiderPayload(data.reminder),
        practice_area: caseDetails?.case_description?.case_practice_area || data?.case?.practice_area || data?.practice_area?.value,
      };

      // Use the utility function to combine date and time
      const { formattedStartTime, formattedEndTime } = combineDateAndTime({
        selectedDate: data.when,
        meetingStartTime: isAllDay ? data.when : data.meeting_start_time,
        meetingEndTime: isAllDay ? data.till : data.meeting_end_time,
        isAllDay,
        timezone: userDetails?.timezone,
      });

      eventData.meeting_start_time = formattedStartTime;
      eventData.meeting_end_time = formattedEndTime;

      if (data.firm_wide) {
        eventData.guests = [];
        delete eventData.case;
        delete eventData.case_id;
        delete eventData.case_name;
        delete eventData.is_lead;
        delete eventData.practice_area;
      }

      if (data?.not_linked_to_case) {
        delete eventData.case;
        delete eventData.case_id;
        delete eventData.case_name;
        delete eventData.is_lead;
      }

      // if no attendees and not firm wide, show error
      if (data?.guests?.length === 0 && !data.firm_wide) {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'At least one attendee is required');
        return;
      }

      delete eventData.firm_users;
      delete eventData.selected_attendees_list;

      if (eventFormMode === 'create') {
        createEvent(eventData, {
          onSuccess: () => {
            closeEventFormModal();
            reset(getDefaultValues());
          },
        });
      } else {
        // Handle recurring event edit
        let occurrence = {};
        if (data?.is_recurring) {
          const isDateChanged = !moment(eventData?.meeting_start_time).isSame(moment(selectedEvent?.meeting_start_time), 'day');

          if (moment(selectedEvent?.recurrence_keys?.recurrence_limit).isSame(eventData?.recurrence_keys?.recurrence_limit, 'day')) {
            // Build categories based on whether dates have changed
            const categories = [
              { key: 'current', label: 'This event' },
              { key: 'futures', label: 'This and following events' },
            ];

            // Only show 'All recurring events' option if dates haven't changed
            if (!isDateChanged) {
              categories.push({ key: 'all', label: 'All recurring events' });
            }

            occurrence = await showConfirmDialog({
              footer: {
                cancelLable: 'Cancel',
                okLable: 'Update',
              },
              formFields: [
                {
                  type: 'title',
                  label: 'Update Event',
                },
                {
                  name: 'selection',
                  type: 'radiobutton',
                  label: 'Option',
                  required: true,
                  categories,
                },
              ],
            }).catch((error) => console.error('Dialog closed or error:', error));
          } else occurrence.selection = 'futures';
          if (!occurrence?.selection) return;

          eventData = {
            ...eventData,
            recurrence_scope: occurrence?.selection,
            overriden_start_date: isAllDay
              ? moment.tz(modals.selectedDate, userDetails?.timezone).format('YYYY-MM-DD')
              : moment.tz(modals.selectedDate, userDetails?.timezone).toISOString(),
            is_date_changed: isDateChanged,
            reminder_updated: !deepEqual(eventData?.reminder, selectedEvent?.reminder),
          };
        }

        // Get the correct event ID from API data
        const eventIdToUpdate = data.event_id || data?.event_rrule_id;

        updateEvent(
          { eventId: eventIdToUpdate, eventData: { ...eventData, sk: selectedEvent.sk, pk: selectedEvent.pk } },
          {
            onSuccess: () => {
              closeEventFormModal();
            },
          }
        );
      }
    } catch (error) {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'An error occurred while processing the form');
    }
  };

  const isSubmitting = isCreating || isUpdating;

  return {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    resetField,
    errors,
    onSubmit,
    isSubmitting,
    eventFormMode,
    closeEventFormModal,
    uploadProgress,
    isUploading,
    attendeesList,
    isLoadingAttendees,
  };
};
